import { Link2, Link2Off, UserRound } from 'lucide-react';
import { formatDateTime, formatRelative } from '../lib/format';
import type { UserAccountSlot } from '../lib/types';
import { Badge, cn } from './ui';

/**
 * A device's in-app accounts: the nicknames the user chose, and which of them
 * ever reached a linked session.
 *
 * Two presentations of the same rows. `compact` is a one-line summary for the
 * user list, where the operator is scanning for "did this person actually get
 * set up?" — and `full` is the timeline in the detail drawer, which also dates
 * each step so the gap between naming a slot and linking it is visible.
 */
export function AccountSlots({
  accounts,
  variant = 'full',
}: {
  accounts: UserAccountSlot[];
  variant?: 'full' | 'compact';
}) {
  if (accounts.length === 0) {
    return (
      <p className="text-xs text-ink-3">
        {variant === 'compact' ? 'No accounts' : 'This device never set up an account.'}
      </p>
    );
  }

  if (variant === 'compact') return <CompactSlots accounts={accounts} />;

  return (
    <ul className="divide-y divide-line rounded-lg border border-line">
      {accounts.map((account) => (
        <li
          key={account.slot_index}
          className="flex items-start justify-between gap-3 px-3 py-2.5"
        >
          <div className="flex min-w-0 items-start gap-2.5">
            <span
              className={cn(
                'mt-0.5 grid size-6 shrink-0 place-items-center rounded-md',
                account.is_connected
                  ? 'bg-good-soft text-good'
                  : 'bg-raised text-ink-3',
              )}
            >
              {account.is_connected ? <Link2 size={13} /> : <Link2Off size={13} />}
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-ink">
                {account.name || `Slot ${account.slot_index}`}
                {!account.name && (
                  <span className="ml-1.5 text-xs font-normal text-ink-3">
                    unnamed
                  </span>
                )}
              </p>
              <p className="mt-0.5 text-xs text-ink-3">
                Slot {account.slot_index}
                {account.named_at
                  ? ` · named ${formatDateTime(account.named_at)}`
                  : ''}
              </p>
            </div>
          </div>

          <div className="shrink-0 text-right">
            {account.is_connected ? (
              <>
                <Badge tone="good">Linked</Badge>
                <p className="mt-1 text-xs text-ink-3">
                  {/* A slot the app found already linked has no believable
                      moment, so say so rather than inventing one. */}
                  {account.connected_at
                    ? formatRelative(account.connected_at)
                    : 'time unknown'}
                </p>
              </>
            ) : (
              <Badge tone="warn">Not linked</Badge>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

/** The list-row form: named accounts as chips, linked ones marked. */
function CompactSlots({ accounts }: { accounts: UserAccountSlot[] }) {
  const named = accounts.filter((account) => account.name?.trim());
  const connected = accounts.filter((account) => account.is_connected).length;

  if (named.length === 0) {
    return (
      <span className="text-xs text-ink-3">
        {accounts.length} unnamed slot{accounts.length === 1 ? '' : 's'}
        {connected > 0 ? ` · ${connected} linked` : ''}
      </span>
    );
  }

  // Two names is what fits a table cell without wrapping; the rest are counted.
  const shown = named.slice(0, 2);
  const rest = named.length - shown.length;

  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {shown.map((account) => (
        <Badge
          key={account.slot_index}
          tone={account.is_connected ? 'good' : 'neutral'}
          icon={
            account.is_connected ? <Link2 size={11} /> : <UserRound size={11} />
          }
        >
          {account.name}
        </Badge>
      ))}
      {rest > 0 && <Badge>+{rest}</Badge>}
    </span>
  );
}
